/**
 * Platform-neutral product-analytics port.
 *
 * The shared signal stores and navigation monitor need to record product-analytics events
 * (page/screen views, conversions) without hard-depending on a platform SDK: web uses the
 * `firebase/analytics` JS SDK, while React Native uses the native `@react-native-firebase/analytics`
 * module (the JS SDK crashes the Metro bundler). This module defines a small `AnalyticsReporter`
 * port plus a module-level holder; each platform injects its adapter at bootstrap (web:
 * `client/src/lib/analytics.ts`, RN: `mobile/src/ports/rn-analytics.ts`).
 *
 * Mirrors the `ErrorReporter` holder pattern in `./error-reporting.ts`.
 *
 * Both back GA4 (Google Analytics 4), so the method surface and value constraints match GA4's:
 * event/param names are snake_case and param values are primitives only.
 */

/** Values GA4 accepts for event params and (as strings) user properties. */
export type AnalyticsParamValue = string | number | boolean | undefined;

/** A bag of GA4 event params. */
export type AnalyticsParams = Record<string, AnalyticsParamValue>;

/** The product-analytics port. */
export interface AnalyticsReporter {
  /** Log a custom event with optional params. Use snake_case names (GA4 convention). */
  logEvent(name: string, params?: AnalyticsParams): void;
  /** Log a page/screen view. Adapters map this to GA4's `screen_view`/`page_view`. */
  logScreenView(screenName: string, params?: AnalyticsParams): void;
  /** Associate subsequent events with a pseudonymous user id (Firebase UID). Pass `undefined` to clear. */
  setUserId(userId: string | undefined): void;
  /** Set low-cardinality, non-PII user properties (values are coerced to strings by GA4). */
  setUserProperties(props: Record<string, string | undefined>): void;
  /**
   * Mark all subsequent events as internal traffic (GA4 `traffic_type=internal`), or clear the mark.
   *
   * Applied via GA4's default event parameters so it also tags automatically-collected events
   * (`session_start`, `user_engagement`, `first_open`) — not just the ones we log explicitly. A GA4
   * "Internal Traffic" data filter matching `traffic_type=internal` can then exclude Elara staff and
   * non-production traffic from reports (and the BigQuery export). This complements the
   * `is_privileged` user property, which stays available for flexible report-time filtering.
   */
  setInternalTraffic(isInternal: boolean): void;
}

/**
 * Default reporter: does nothing. Used until a platform injects a real reporter, on platforms
 * where analytics is not configured, and in tests. It also means "analytics off" is the safe
 * default — a consent gate can simply withhold `setAnalyticsReporter()`.
 */
export const noopAnalyticsReporter: AnalyticsReporter = {
  logEvent() {},
  logScreenView() {},
  setUserId() {},
  setUserProperties() {},
  setInternalTraffic() {},
};

let currentReporter: AnalyticsReporter = noopAnalyticsReporter;

/** Injects the active analytics reporter (call once at app bootstrap). */
export function setAnalyticsReporter(reporter: AnalyticsReporter): void {
  currentReporter = reporter;
}

/** Returns the active analytics reporter. */
export function getAnalyticsReporter(): AnalyticsReporter {
  return currentReporter;
}
